import React, { useState, useEffect, useRef } from 'react';
import { soundManager } from '../../utils/soundManager';
import './Collections.css';

const Collections = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const collections = [
    {
      id: 'royal',
      name: 'Royal Series',
      tagline: 'Haute Horlogerie',
      image: '/images/collections/royal-series.png',
      features: [
        'Perpetual Calendar',
        'Moon Phase',
        'Tourbillon Movement',
        '18K Gold Case'
      ],
      priceRange: 'From $45,000',
      pieces: 'Limited to 50 pieces'
    },
    {
      id: 'sport',
      name: 'Sport Series',
      tagline: 'Performance & Precision',
      image: '/images/collections/sport-series.png',
      features: [
        'Chronograph Function',
        'Ceramic Bezel',
        'Water Resistant 300m',
        'Automatic Movement'
      ],
      priceRange: 'From $18,000',
      pieces: 'Limited to 200 pieces'
    },
    {
      id: 'classic',
      name: 'Classic Series',
      tagline: 'Timeless Elegance',
      image: '/images/collections/classic-series.png',
      features: [
        'Ultra-Luxurious Profile',
        'Date Complication',
        'Alligator Leather',
        'Swiss Movement'
      ],
      priceRange: 'From $12,000',
      pieces: 'Limited to 300 pieces'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleCardClick = (collectionName) => {
    soundManager.play('ui-click', 0.4);
    console.log(`Exploring ${collectionName}`);
    // In a real application, this would navigate to collection detail page
  };

  return (
    <section 
      id="collections" 
      ref={sectionRef}
      className={`collections-section ${isVisible ? 'visible' : ''}`}
    >
      <div className="collections-container">
        {/* Section Header */}
        <div className="collections-header">
          <div className="header-label">
            <span className="label-line"></span>
            <span className="label-text">Our Collections</span>
            <span className="label-line"></span>
          </div>
          <h2 className="collections-title">Masterpieces of Time</h2>
          <p className="collections-subtitle">
            Each collection represents decades of Swiss watchmaking excellence, 
            combining traditional craftsmanship with modern innovation.
          </p>
        </div>

        {/* Collections Grid */}
        <div className="collections-grid">
          {collections.map((collection, index) => (
            <div
              key={collection.id}
              className="collection-card"
              style={{
                transitionDelay: `${index * 0.2}s`
              }}
            >
              <div className="card-inner">
                {/* Front of card */}
                <div className="card-front">
                  <div className="card-image-container">
                    <img 
                      src={collection.image} 
                      alt={collection.name}
                      className="card-image"
                    />
                    <div className="card-overlay"></div>
                  </div>
                  <div className="card-front-content">
                    <span className="card-tagline">{collection.tagline}</span>
                    <h3 className="card-name">{collection.name}</h3>
                    <span className="card-price">{collection.priceRange}</span>
                  </div>
                  <div className="card-hover-indicator">
                    <span>Hover to Explore</span>
                  </div>
                </div>

                {/* Back of card */}
                <div className="card-back">
                  <div className="card-back-content">
                    <h3 className="back-title">{collection.name}</h3>
                    <span className="back-tagline">{collection.tagline}</span>
                    
                    <ul className="card-features">
                      {collection.features.map((feature, idx) => (
                        <li key={idx} className="feature-item">
                          <span className="feature-icon">◆</span>
                          <span className="feature-text">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="card-back-footer">
                      <div className="back-pieces">{collection.pieces}</div>
                      <button 
                        className="explore-btn"
                        onClick={() => handleCardClick(collection.name)}
                      >
                        View Collection
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="collections-cta">
          <p className="cta-text">
            Schedule a private viewing to experience these timepieces in person
          </p>
          <button 
            className="cta-button"
            onClick={() => {
              soundManager.play('ui-click', 0.4);
              document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Request Appointment
          </button>
        </div>
      </div>
    </section>
  );
};

export default Collections;